import { UUID } from "../../../src/app/core/interfaces";

/**
 * Splits an array into smaller chunks of a specified size.
 *
 * @template T - The type of elements in the array
 * @param {Array<T>} array - The array to be chunked
 * @param {number} size - The size of each chunk
 * @return {Array<Array<T>>} A new array containing chunks of the original array
 * 
 * @throws {Error} When chunk size is not a positive integer
 */
export const chunkArray = <T>(array: T[], size: number): T[][] => {
  if (!Number.isInteger(size) || size < 1) {
    throw new Error("Chunk size must be a positive integer");
  }
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export const SYSTEM_USER_ID = "cloud-function" as UUID;

/**
 * Creates an update object for system-generated modifications that automatically
 * sets the `lastModifiedBy` field to the system user ID ('cloud-function').
 *
 * @template T - Type that extends an object with a `lastModifiedBy` property of type UUID
 * @param {Partial<Omit<T, 'lastModifiedBy'>>} data - The data to be updated, excluding the `lastModifiedBy` property
 * @return {Partial<T>} A new object containing the provided data plus the system user ID as `lastModifiedBy`
 * 
 */
export function createSystemUpdate<T extends { lastModifiedBy: UUID }>(
  data: Partial<Omit<T, "lastModifiedBy">>
): Partial<T> {
  return {
    ...data,
    lastModifiedBy: SYSTEM_USER_ID
  } as Partial<T>;
}
